import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  getAktivitaGaPagesSummary,
  getAktivitaGaPagesTrend,
  getAktivitaGaPages,
  type AktivitaPeriod,
  type AktivitaCountry,
  type GaPagesSummary,
  type GaPagesTrend,
  type GaPages,
  type GaPageItem,
} from '../../api/rondo';
import { ArcSelect, ArcTabs, ArcToolbar } from '../../components/arc/primitives/arc-primitives';
import { Card } from '../../components/arc/card/card';
import { Button } from '../../components/arc/button/button';
import { PageState } from '../../components/arc/page-state/page-state';
import { SortableDataTable, type DataColumn } from '../../components/arc/sortable-data-table/sortable-data-table';
import {
  DeltaArrow,
  TrendChart,
  fmtNum,
  fmtDuration,
  fmtPct,
  fmtValue,
  type KpiFmt,
} from './aktivita-shared';
import './aktivita-drill.css';

type ProductKey = 'tipovacka' | 'souteze' | 'hry';
const PRODUCTS: Record<ProductKey, { label: string; prefix: string; color: string }> = {
  tipovacka: { label: 'Tipovačka', prefix: '/tipovacka', color: '#7c3aed' },
  souteze: { label: 'Soutěže', prefix: '/souteze', color: '#1d4ed8' },
  hry: { label: 'Hry', prefix: '/hra', color: '#137333' },
};

interface ProdKpi {
  key: keyof GaPagesSummary['current'];
  label: string;
  fmt: KpiFmt;
  lowerIsBetter?: boolean;
}
const KPIS: ProdKpi[] = [
  { key: 'views', label: 'Zobrazení', fmt: 'num' },
  { key: 'page_count', label: 'Aktivní stránky', fmt: 'num' },
  { key: 'avg_time_on_page_sec', label: 'Avg. čas', fmt: 'duration' },
  { key: 'bounce_rate', label: 'Bounce', fmt: 'pct', lowerIsBetter: true },
];

function prevText(k: ProdKpi, prev: number): string {
  switch (k.fmt) {
    case 'duration':
      return fmtDuration(prev);
    case 'pct':
      return fmtPct(prev, 1);
    default:
      return fmtNum(prev);
  }
}

export function AktivitaProduktyPage() {
  const [product, setProduct] = useState<ProductKey>('tipovacka');
  const [period, setPeriod] = useState<AktivitaPeriod>('month');
  const [country, setCountry] = useState<AktivitaCountry>('all');

  const prefix = PRODUCTS[product].prefix;
  const color = PRODUCTS[product].color;

  const [summary, setSummary] = useState<GaPagesSummary | null>(null);
  const [trend, setTrend] = useState<GaPagesTrend | null>(null);
  const [pages, setPages] = useState<GaPages | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getAktivitaGaPagesSummary({ period, country, pathPrefix: prefix }),
      getAktivitaGaPagesTrend({ period, country, pathPrefix: prefix }),
      getAktivitaGaPages({ period, country, pathPrefix: prefix, limit: 30 }),
    ])
      .then(([s, t, p]) => {
        if (cancelled) return;
        setSummary(s);
        setTrend(t);
        setPages(p);
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [product, period, country, prefix]);

  const trendPoints = trend?.points ?? [];
  const labels = trendPoints.map((p) => p.date);
  const datasets = [
    {
      label: `Zobrazení ${prefix}*`,
      data: trendPoints.map((p) => Number(p.views ?? 0)),
      borderColor: color,
      backgroundColor: color + '22',
      fill: true,
    },
  ];
  const tFirst = trendPoints[0];
  const tLast = trendPoints[trendPoints.length - 1];
  const trendRange = tFirst && tLast ? `${tFirst.date} – ${tLast.date} (${trendPoints.length} dní)` : 'žádná data';
  type PageRow = GaPageItem & { rank: number; title: string } & Record<string, unknown>;
  const pageRows: PageRow[] = (pages?.items ?? []).map((item, index) => ({ ...item, rank: index + 1, title: item.page_title || item.page_path }));
  const pageColumns: DataColumn<PageRow>[] = [
    { key: 'rank', label: '#', width: 65, numeric: true },
    { key: 'title', label: 'Stránka', width: 550, render: (_, row) => <><strong>{row.title}</strong><small>{row.page_path}</small></> },
    { key: 'views', label: 'Zobrazení', width: 150, numeric: true, render: (_, row) => fmtNum(row.views) },
    { key: 'avg_time_on_page_sec', label: 'Průměrný čas', width: 170, numeric: true, render: (_, row) => fmtDuration(row.avg_time_on_page_sec) },
    { key: 'bounce_rate', label: 'Opuštění', width: 150, numeric: true, render: (_, row) => fmtPct(row.bounce_rate, 1) },
  ];

  return (
    <section className="activity-custom-page activity-products-page rondo-arc-activity-drill p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">AKTIVITA</div><h1>Produkty</h1><p>Výkon produktových částí Ronda a chování návštěvníků</p></div></header>
      <ArcTabs label="Produkt" value={product} onChange={setProduct} items={(Object.keys(PRODUCTS) as ProductKey[]).map((key) => ({ value: key, label: PRODUCTS[key].label }))} />
      <ArcToolbar className="rondo-arc-activity-toolbar">
        <ArcSelect label="Období" value={period} onChange={(value) => setPeriod(value as AktivitaPeriod)} options={[{ value: 'day', label: 'Den' }, { value: 'week', label: 'Týden' }, { value: 'month', label: 'Měsíc' }, { value: 'year', label: 'Rok' }]} />
        <ArcSelect label="Trh" value={country} onChange={(value) => setCountry(value as AktivitaCountry)} options={[{ value: 'all', label: 'Vše' }, { value: 'cz', label: 'CZ' }, { value: 'sk', label: 'SK' }, { value: 'ro', label: 'RO' }]} />
        <Button variant="ghost" size="sm" type="button" onClick={() => { setPeriod('month'); setCountry('all'); }}>Vyčistit</Button>
      </ArcToolbar>

      {(country === 'sk' || country === 'ro') && (
        <div className="rondo-arc-activity-notice">
          rondo.{country} nemá GA tracking – data v této sekci jsou jen pro CZ. SEO data pro
          SK/RO najdeš v <a href="/aktivita/seo" className="alert-link">SEO (Ahrefs)</a> a{' '}
          <a href="/aktivita/vyhledavani" className="alert-link">Vyhledávání (GSC)</a>.
        </div>
      )}

      {error ? <PageState title="Produktová data se nepodařilo načíst" message={error} /> : (
        <div className="rondo-arc-activity-drill-kpis">
          {KPIS.map((k) => {
            const curr = Number(summary?.current?.[k.key] ?? 0);
            const prev = Number(summary?.previous?.[k.key] ?? 0);
            return (
              <Card title={k.label} className="rondo-arc-activity-drill-kpi" key={k.key as string}>
                <strong>{fmtValue(curr, k.fmt)}</strong>
                <p><span>Minulé období {prevText(k, prev)}</span>{summary ? <DeltaArrow curr={curr} prev={prev} lowerIsBetter={k.lowerIsBetter} /> : <span>—</span>}</p>
              </Card>
            );
          })}
        </div>
      )}

      <Card title="Trend zobrazení" description={trendRange} className="rondo-arc-activity-trend"><div><TrendChart labels={labels} datasets={datasets} height={240} /></div></Card>

      <div className="rondo-arc-activity-pages-heading"><h2>Top stránky {prefix}*</h2><span>{pageRows.length} stránek</span></div>
      {loading ? <PageState title="Načítám stránky" loading /> : <div className="rondo-arc-activity-pages-table"><SortableDataTable rows={pageRows} columns={pageColumns} rowKey={(row) => `${row.page_path}-${row.rank}`} caption={`Top stránky ${prefix}`} emptyMessage={`Žádné stránky ${prefix}* za toto období`} /></div>}
    </section>
  );
}
